/**
 * useFlows — communications-flow library data hook (Greg's naming shape)
 * re-grafted onto the live CRUD wiring (#728).
 *
 * Reads through `listBuilderFlows`; the demo seed is EMPTY per the seed-guard
 * (example flows come from the mock layer, REQ-002/REQ-017). Mutations call
 * createBuilderFlow/updateBuilderFlow/deleteBuilderFlow/duplicateBuilderFlow,
 * AWAITED, before the cache updates.
 */
import { t as i18nT } from "../../lib/i18n";
import { useEffect, useState } from "react";
import { listBuilderFlows, createBuilderFlow, updateBuilderFlow, deleteBuilderFlow, duplicateBuilderFlow } from "@v0y4ge/api-client";
import { FB_TRIGGER, FB_TRIGGERS } from "../builder/fb-model";
import type { SeedFlow } from "./flows-seed";
import { logUpsert, logDelete } from "../../lib/audit";
import { onTenantChange, onTenantSwitching } from "../../shell/access";

export { FLOW_KIND_PILL, flowStats } from "./flows-seed";
export type { SeedFlow } from "./flows-seed";

/** Resolve a trigger label, tolerating the seed's `registers` id. */
export function flowTrigLabel(ev?: string): string {
  const t = FB_TRIGGERS.find((x) => x.id === ev);
  if (t) return t.label;
  if (ev === "registers" || ev === "registered") return "Guest registers";
  return ev ? FB_TRIGGER(ev).label : "—";
}

function toFlow(f: Record<string, any>): SeedFlow {
  return {
    id: f.id,
    name: f.name ?? "",
    eventKind: f.eventKind ?? f.event_kind ?? "invite",
    desc: f.desc ?? f.description ?? "",
    trigger: f.trigger ?? { event: "invited" },
    steps: f.steps ?? f.definition?.steps ?? [],
  };
}

let _cache: SeedFlow[] | null = null;
let _error: string | null = null;
const _listeners = new Set<() => void>();
const notify = () => _listeners.forEach((fn) => fn());
onTenantSwitching(() => { _cache = null; _error = null; notify(); });
onTenantChange(() => { void loadAll(); });

type AnyFn = (...a: unknown[]) => Promise<unknown>;
const _op = (fn: unknown): AnyFn | null => (typeof fn === "function" ? (fn as AnyFn) : null);

async function loadAll() {
  _error = null;
  try {
    const r = await listBuilderFlows();
    const arr: Record<string, any>[] = ((r as any)?.data ?? (r as any)?.flows ?? []);
    _cache = arr.length ? arr.map(toFlow) : [];
  } catch (e) {
    // drop24 docket item 5: a load/parse failure must surface a real error state,
    // not silently render as "0 of 0". Distinguish failure from a genuine empty set.
    console.warn("listBuilderFlows failed:", e);
    _error = "Couldn't load flows. Retry in a moment.";
    _cache = [];
  }
  notify();
}

export function useFlows() {
  const [, setV] = useState(0);
  useEffect(() => {
    const fn = () => setV((v) => v + 1);
    _listeners.add(fn);
    if (!_cache) loadAll();
    return () => { _listeners.delete(fn); };
  }, []);

  return {
    all: (): SeedFlow[] => _cache ?? [],
    loading: _cache === null,
    error: _error,
    get: (id: string) => (_cache ?? []).find((f) => f.id === id) ?? null,
    newFlow: (): SeedFlow => ({ id: `flow_${Date.now().toString(36)}`, name: "", eventKind: "invite", desc: "", trigger: { event: "invited" }, steps: [] }), // ui-local — unsaved draft shape
    /** Awaited client write first; cache+notify only on success (#728). */
    upsert: async (f: SeedFlow): Promise<SeedFlow> => {
      const idx = (_cache ?? []).findIndex((x) => x.id === f.id);
      const saved = { ...f };
      const op = _op(idx >= 0 ? updateBuilderFlow : createBuilderFlow);
      if (op) {
        const res = (idx >= 0 ? await op(f.id, saved) : await op(saved)) as Record<string, any> | undefined;
        const sid = res?.id ?? res?.flow?.id;
        if (idx < 0 && sid) saved.id = String(sid);
      }
      const j = (_cache ?? []).findIndex((x) => x.id === f.id);
      logUpsert("flow", j >= 0 ? ((_cache ?? [])[j] as unknown as Record<string, unknown>) : null, saved as unknown as Record<string, unknown>, { name: saved.name });
      _cache = j >= 0 ? (_cache ?? []).map((x) => (x.id === f.id ? saved : x)) : [saved, ...(_cache ?? [])];
      notify();
      return saved;
    },
    duplicate: async (id: string): Promise<SeedFlow | null> => {
      const s = (_cache ?? []).find((x) => x.id === id);
      if (!s) return null;
      const c: SeedFlow = { ...JSON.parse(JSON.stringify(s)), id: `flow_${Date.now().toString(36)}`, name: i18nT("messaging.useFlows.sNameCopy.icu", { value0: s.name }) };
      const op = _op(duplicateBuilderFlow);
      if (op) {
        const res = await op(id) as Record<string, any> | undefined;
        const sid = res?.id ?? res?.flow?.id;
        if (sid) c.id = String(sid);
      }
      _cache = [c, ...(_cache ?? [])];
      notify();
      return c;
    },
    remove: async (id: string) => {
      const op = _op(deleteBuilderFlow);
      if (op) await op(id);
      const prev = (_cache ?? []).find((x) => x.id === id);
      if (prev) logDelete("flow", prev as unknown as Record<string, unknown>, { name: prev.name });
      _cache = (_cache ?? []).filter((x) => x.id !== id);
      notify();
    },
    reload: () => { _cache = null; loadAll(); },
  };
}
